// Promise-based styled replacement for window.confirm(). The native dialog
// can't format the multi-line consent/billing attestations Onboard and Studio
// need, truncates on some browsers, and looks foreign next to the app's own
// review modals. Mount <ConfirmDialogHost /> ONCE at the app root; call sites
// do `if (!(await confirmDialog({ ... }))) return;` — a drop-in for confirm()
// inside the async handlers that used it.
import React, { useEffect, useRef, useState } from 'react';

let _open = null; // set by the mounted host; module-level so call sites need no context

function _hasFields(opts) {
  return !!opts && Array.isArray(opts.fields) && opts.fields.length > 0;
}

// confirmDialog({ title, body?, bullets?, fields?, confirmLabel?, cancelLabel?, danger? })
//   → Promise<boolean>                       (no `fields` — backward compatible)
//   → Promise<{ ok: boolean, values: {} }>   (when `fields` is provided)
// `bullets` renders as a list — the shape the consent attestations need.
// `fields[i]` = { key, label, type: 'date'|'select'|'textarea', options?, required?, max? };
// select `options` = [{ value, label }]. On confirm the collected values are
// returned keyed by `field.key`. Falls back to window.confirm when the host
// isn't mounted (never silently true); WITH fields it fails CLOSED
// ({ ok:false }) because a native confirm can't collect structured input.
export function confirmDialog(opts) {
  if (!_open) {
    if (_hasFields(opts)) return Promise.resolve({ ok: false, values: {} });
    const text = [opts.title, opts.body, ...(opts.bullets || []).map((b) => `• ${b}`)].filter(Boolean).join('\n\n');
    return Promise.resolve(window.confirm(text));
  }
  return _open(opts);
}

export function ConfirmDialogHost() {
  const [state, setState] = useState(null); // { opts, resolve }
  const [values, setValues] = useState({}); // field values for the OPEN dialog
  const stateRef = useRef(null);            // mirror for unmount cleanup
  const dialogRef = useRef(null);
  const confirmRef = useRef(null);
  const firstFieldRef = useRef(null);
  const restoreFocusRef = useRef(null);
  stateRef.current = state;

  // Resolve a pending promise with the shape its caller expects: object when
  // that dialog declared fields, bare boolean otherwise (backward compatible).
  const _resolveFor = (entry, ok, vals) => {
    if (!entry) return;
    if (_hasFields(entry.opts)) entry.resolve({ ok, values: ok ? (vals || {}) : {} });
    else entry.resolve(ok);
  };

  useEffect(() => {
    _open = (opts) => new Promise((resolve) => {
      restoreFocusRef.current = document.activeElement;
      const seed = {};
      (opts.fields || []).forEach((f) => { seed[f.key] = f && f.value != null ? f.value : ''; });
      setValues(seed);
      // A second confirm while one is open cancels the first (shouldn't happen —
      // callers are button-gated — but never leave a promise hanging).
      setState((prev) => { _resolveFor(prev, false); return { opts, resolve }; });
    });
    return () => {
      _open = null;
      // Unmount with a dialog open: resolve the pending promise (as cancel) so
      // the awaiting handler never hangs.
      _resolveFor(stateRef.current, false);
    };
  }, []);

  useEffect(() => {
    if (!state) return;
    // Data-collecting dialogs open with focus on the first field (its confirm
    // button may start disabled). Plain confirms focus the confirm button.
    if (_hasFields(state.opts) && firstFieldRef.current) firstFieldRef.current.focus();
    else if (confirmRef.current) confirmRef.current.focus();
  }, [state]);

  if (!state) return null;
  const { opts } = state;
  const hasFields = _hasFields(opts);
  // Confirm stays disabled while any field is invalid. Required-but-empty is
  // invalid; a date outside [min,max] is invalid too — the `max` attribute only
  // constrains the native picker, so we fail CLOSED here (a future
  // initiating-visit date must never reach a billing write). YYYY-MM-DD strings
  // order lexicographically, so string comparison is correct.
  const fieldInvalid = (f) => {
    const v = String(values[f.key] || '').trim();
    if (f.required && !v) return true;
    if (v && f.type === 'date') {
      if (f.max && v > f.max) return true;
      if (f.min && v < f.min) return true;
    }
    return false;
  };
  const blockConfirm = hasFields && opts.fields.some(fieldInvalid);
  const close = (ok) => {
    _resolveFor(state, ok, { ...values });
    setState(null);
    const el = restoreFocusRef.current;
    if (el && typeof el.focus === 'function') el.focus();
    restoreFocusRef.current = null;
  };

  // Minimal focus trap: Tab cycles within the dialog's focusable controls so
  // Escape/Enter always land here, never on background controls under the
  // backdrop. Disabled controls (e.g. a confirm button awaiting required
  // fields) are excluded so focus never lands on an unusable element.
  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.stopPropagation(); close(false); return; }
    if (e.key !== 'Tab') return;
    const focusables = dialogRef.current
      ? Array.from(dialogRef.current.querySelectorAll('button, input, select, textarea')).filter((el) => !el.disabled)
      : [];
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  const setField = (key, val) => setValues((v) => ({ ...v, [key]: val }));

  return (
    <div className="modal-backdrop confirm-dialog-backdrop" onClick={() => close(false)}>
      <div ref={dialogRef} className="modal confirm-dialog" role="dialog" aria-modal="true" aria-label={opts.title}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}>
        <div className="modal-head"><h3>{opts.title}</h3></div>
        <div className="modal-body">
          {opts.body && <p className="confirm-dialog-body">{opts.body}</p>}
          {Array.isArray(opts.bullets) && opts.bullets.length > 0 && (
            <ul className="confirm-dialog-bullets">
              {opts.bullets.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          )}
          {hasFields && (
            <div className="confirm-dialog-fields">
              {opts.fields.map((f, i) => (
                <div className="field" key={f.key}>
                  <div className="field-label">{f.label}{f.required ? ' *' : ''}</div>
                  {f.type === 'select' ? (
                    <select
                      ref={i === 0 ? firstFieldRef : undefined}
                      value={values[f.key] || ''}
                      onChange={(e) => setField(f.key, e.target.value)}>
                      <option value="">{f.placeholder || 'Select…'}</option>
                      {(f.options || []).map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  ) : f.type === 'textarea' ? (
                    <textarea
                      ref={i === 0 ? firstFieldRef : undefined}
                      rows={4}
                      value={values[f.key] || ''}
                      onChange={(e) => setField(f.key, e.target.value)} />
                  ) : (
                    <input
                      ref={i === 0 ? firstFieldRef : undefined}
                      type={f.type === 'date' ? 'date' : 'text'}
                      value={values[f.key] || ''}
                      max={f.type === 'date' ? f.max : undefined}
                      onChange={(e) => setField(f.key, e.target.value)} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn ghost" onClick={() => close(false)}>{opts.cancelLabel || 'Cancel'}</button>
          <button ref={confirmRef} className={`btn ${opts.danger ? 'danger' : 'primary'}`}
            disabled={blockConfirm}
            onClick={() => close(true)}>
            {opts.confirmLabel || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}
